import { SINGLE_CAMERA } from './copReducers';
import type { CopVideoStream } from './types';
import type { ManualStageLayout } from '@/lib/manual-layout';

/**
 * What FEED says about a camera the stage is drawing, by where the stage put
 * it. One sentence per placement, because "beside the map" is a lie in the
 * MAP layout (a corner inset) and in VIDEO (the picture IS the stage).
 */
export function stagePlacementSentence(placement: ManualStageLayout): string {
  switch (placement) {
    case 'split':
      return 'Showing beside the map.';
    case 'map':
      return "Showing in the map's corner.";
    case 'video':
      return 'Showing on the stage.';
  }
}

/**
 * Which camera a window key actually points at, on THIS aircraft.
 *
 * `SINGLE_CAMERA` means two things that resolve to one: the only feed of an
 * aircraft that registered a single unnamed camera, and "the default camera" —
 * which is what the window opened by a map click carries, because a click on
 * the map is not a choice of camera. `CopVideoWindow` resolves both to
 * `streams[0]`, so everything that asks "which camera is in a window" has to
 * resolve them the same way.
 *
 * It is what stops a two-camera airframe from reading its nose camera as free
 * while a window is already showing it: the panel would offer to open a second
 * window on the same stream, and the stage would decode it a second time, on a
 * link that is often the aircraft's own.
 */
export function resolveCameraKey(streams: CopVideoStream[], cameraKey: string): string {
  if (cameraKey !== SINGLE_CAMERA || streams.length === 0) return cameraKey;
  return streams[0].camera_id ?? SINGLE_CAMERA;
}

/** The cameras of this aircraft that some window is drawing, by their own keys
 *  rather than by the keys the windows were opened under. */
function drawnInWindows(streams: CopVideoStream[], poppedCameras: string[]): Set<string> {
  return new Set(poppedCameras.map((key) => resolveCameraKey(streams, key)));
}

/** Is this camera's picture in a window right now? */
export function isCameraPopped(
  streams: CopVideoStream[],
  poppedCameras: string[],
  cameraKey: string,
): boolean {
  return drawnInWindows(streams, poppedCameras).has(resolveCameraKey(streams, cameraKey));
}

/**
 * Whether closing `cameraKey`'s window hands the picture back to the STAGE:
 * the stage draws the first camera not in a window, so once this one leaves the
 * popped set it is the stage's next pick unless an earlier camera is already
 * there. The dock verb has to say which, or it promises a picture that never
 * arrives.
 */
export function stageTakesBack(
  streams: CopVideoStream[],
  poppedCameras: string[],
  cameraKey: string,
): boolean {
  const target = resolveCameraKey(streams, cameraKey);
  // Resolved BEFORE the removal, so taking "nose" out of the set really takes
  // out the default-camera window that is showing it.
  const remaining = [...drawnInWindows(streams, poppedCameras)].filter((c) => c !== target);
  const next = chooseStream(streams, remaining).stream;
  return next !== null && (next.camera_id ?? SINGLE_CAMERA) === target;
}

/**
 * Which camera the stage draws: the first one that is not already in a window.
 *
 * Pure over its two inputs, and exported for that reason: the stage
 * (`ManualVideoStage`) uses it to pick, and the FEED segment uses it to know
 * which camera the stage picked, so it can print a sentence instead of a second
 * player. One stream is never decoded twice for the same operator, and the two
 * surfaces agree because they run the same function on the same facts rather
 * than one reporting to the other (split-video record, D6).
 *
 * `allPopped` is reported separately from "there is no stream", because they
 * are different facts and only one of them means the aircraft has no camera.
 */
export function chooseStream(
  streams: CopVideoStream[],
  poppedCameras: string[],
): { stream: CopVideoStream | null; allPopped: boolean } {
  if (streams.length === 0) return { stream: null, allPopped: false };
  const drawn = drawnInWindows(streams, poppedCameras);
  const available = streams.filter((s) => !drawn.has(s.camera_id ?? SINGLE_CAMERA));
  if (available.length === 0) return { stream: null, allPopped: true };
  return { stream: available[0], allPopped: false };
}
